import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MEN_HEALTH } from 'src/app/constants/health-history.constants';
import { SpinnerComponent } from 'src/app/modules/shared/spinner/spinner.component';
import { LookupService } from 'src/app/services/ha-new/lookup.service';
import { MedicalHistoryService } from 'src/app/services/ha-new/medical-history.service';
import { NewHAService } from 'src/app/services/ha-new/new-ha.service';

/**
 * men's health component
 */
@Component({
	selector: 'app-men-health',
	templateUrl: './men-health.component.html',
	styleUrls: ['./men-health.component.scss'],
})
export class MenHealthComponent implements OnInit {
	/** holds men's health concerns lookup data */
	healthConcernsLookup: any = [];
	/** Data carrier subscriptions */
	subscriptions: Subscription[] = [];
	/** holds appointment id */
	apptID: any;
	/** holds readOnly flag */
	readOnly: boolean = false;
	/** holds ha id */
	healthAssessmentID: number;
	/** holds scroll values */
	scrollTo = '';
	/** holds other concerns form field */
	otherConcerns: FormControl = new FormControl('');
	/** holds concerns to be passed to backend */
	menHealthConcerns: any = [];
	/** decides if other text box has to be shown or not */
	showOtherTextBox: boolean = false;
	/** holds enum */
	menHealthEnum = MEN_HEALTH;
	/** holds men health form */
	menHealthForm: FormGroup;
	returningUser: boolean = false;

	/**
	 * constructor
	 * @param lookupService
	 * @param medicalHistoryService
	 * @param spinner
	 * @param activatedRoute
	 * @param router
	 * @param haService
	 */
	constructor(
		private lookupService: LookupService,
		private medicalHistoryService: MedicalHistoryService,
		private spinner: SpinnerComponent,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private haService: NewHAService
	) {
		this.activatedRoute.queryParams.subscribe(params => {
			this.apptID = params.apptID;
			this.readOnly = params.readOnly === 'true';
			this.returningUser = params.returningUser === 'true';
			if (this.readOnly) {
				this.otherConcerns.disable();
			}
			this.healthAssessmentID = params.healthAssessmentID;
		});
	}

	/**
	 * on component initialization
	 */
	ngOnInit() {
		this.getMensHealthConcernsLookup();
		this.initializeMenHealthForm();
	}

	/**
	 * after view checked - initializing scroll
	 */
	ngAfterViewChecked() {
		if (this.scrollTo != 'false') {
			if (this.haService.scrollView(this.scrollTo)) {
				this.scrollTo = 'false';
			}
		}
	}

	/**
	 * initializes men health form
	 */
	initializeMenHealthForm() {
		this.menHealthForm = new FormGroup({
			sexuallyActive: new FormControl({ value: '', disabled: this.readOnly }),
			hadStd: new FormControl({ value: '', disabled: this.readOnly }),
			wantToTestForStd: new FormControl({ value: '', disabled: this.readOnly }),
			sexualOption: new FormControl({ value: '', disabled: this.readOnly }),
			stdDetails: new FormControl({ value: '', disabled: this.readOnly }),
			noOfSexualPartners: new FormControl({
				value: '',
				disabled: this.readOnly,
			}),
		});
	}

	/**
	 * gets mens health concerns lookup data
	 */
	getMensHealthConcernsLookup() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.lookupService.getMensHealthConcerns().subscribe(
				res => {
					this.spinner.stopLoader();
					this.healthConcernsLookup = res.data;
					console.log(this.healthConcernsLookup);
					this.getMensHealthData();
				},
				err => {
					this.spinner.stopLoader();
					console.error(`[${MenHealthComponent.name}][${this.getMensHealthConcernsLookup.name}]`, 'Error when getting mens health concerns lookup data ', err);
				}
			)
		);
	}

	/**
	 * gets men health data from service
	 */
	getMensHealthData() {
		this.spinner.startLoader();
		this.medicalHistoryService.getMenHealth(this.apptID, this.healthAssessmentID).subscribe(
			res => {
				console.log(res);
				this.spinner.stopLoader();
				if (res && res.data && res.data.concerns) {
					res.data.concerns.forEach(concern => {
						this.menHealthConcerns.push(concern);
						const index = this.healthConcernsLookup.findIndex(healthConcern => healthConcern.id === concern.optionID);
						this.healthConcernsLookup[index].checked = true;
						// if other is selected setting flag to display other text box
						if (concern.optionID === MEN_HEALTH.OTHER) {
							this.showOtherTextBox = true;
							this.otherConcerns.setValue(concern.optionValue);
						}
					});
				}
				this.menHealthForm.controls.sexuallyActive.setValue(res.data.sexuallyActive);
				this.menHealthForm.controls.hadStd.setValue(res.data.hadStd);
				this.menHealthForm.controls.wantToTestForStd.setValue(res.data.wantToTestForStd);
				this.menHealthForm.controls.sexualOption.setValue(res.data.sexualOption);
				this.menHealthForm.controls.stdDetails.setValue(res.data.stdDetails);
				this.menHealthForm.controls.noOfSexualPartners.setValue(res.data.noOfSexualPartners);
			},
			err => {
				this.spinner.stopLoader();
				console.error(`[${MenHealthComponent.name}][${this.getMensHealthData.name}]`, 'Error occurred while getting men health ', err);
			}
		);
	}

	/**
	 * on selecting men health concern
	 * @param condition
	 */
	healthConcernSelection(event, concern) {
		console.log(event, concern);
		// Pushing into array if checked
		if (event.checked) {
			// showing text fields if cancer/other is selected
			if (concern.id === MEN_HEALTH.OTHER) {
				this.showOtherTextBox = true;
			}
			this.menHealthConcerns.push({ optionID: concern.id, name: concern.name });
		} else {
			// removing from array if unchecked
			concern.checked = false;
			const index = this.menHealthConcerns.findIndex(element => element.optionID === concern.id);
			this.menHealthConcerns.splice(index, 1);
			if (concern.id === MEN_HEALTH.OTHER) {
				this.showOtherTextBox = false;
			}
		}
	}

	/**
	 * saves men health
	 */
	next() {
		if (!this.readOnly) {
			if (this.otherConcerns.value) {
				const index = this.menHealthConcerns.findIndex(concern => concern.optionID == MEN_HEALTH.OTHER);
				if (index >= 0) {
					this.menHealthConcerns[index].optionValue = this.otherConcerns.value;
				}
			}
			if (!this.menHealthForm.getRawValue().hadStd) {
				this.menHealthForm.controls.stdDetails.setValue('');
			}
			console.log(this.menHealthConcerns);
			this.medicalHistoryService
				.saveMenHealth(this.apptID, this.healthAssessmentID, {
					...this.menHealthForm.getRawValue(),
					concerns: this.menHealthConcerns,
				})
				.subscribe(
					res => {
						this.spinner.stopLoader();
						console.log(res);
					},
					err => {
						this.spinner.stopLoader();
						console.error(`[${MenHealthComponent.name}][${this.next.name}]`, 'Error occurred while saving mens health ', err);
					}
				);
			this.haService.redirect(
				[],
				['family-history'],
				[],
				this.apptID,
				this.healthAssessmentID,
				this.readOnly,
				'medical-family-history',
				this.returningUser
			);
		}
	}

	/**
	 * Destroys the component
	 */
	ngOnDestroy() {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}
}
